import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, HandCoins, Trash2, Wallet } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function Kasbon() {
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", amount: "", reason: "", date: today() });
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");

  const load = () => api.get("/kasbon").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/users?role=karyawan").then((r) => setEmployees(r.data));
  }, []);

  const submit = async () => {
    if (!form.employee_id || !form.amount) { toast.error("Pilih karyawan dan isi jumlah"); return; }
    try {
      await api.post("/kasbon", { ...form, amount: parseFloat(form.amount) });
      toast.success("Kasbon dicatat");
      setOpen(false);
      setForm({ employee_id: "", amount: "", reason: "", date: today() });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pay = async () => {
    if (!payAmount) { toast.error("Isi jumlah pembayaran"); return; }
    try {
      await api.patch(`/kasbon/${payFor._id}/pay`, { pay_amount: parseFloat(payAmount) });
      toast.success("Pembayaran dicatat");
      setPayFor(null); setPayAmount("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      const res = await api.post("/deletion-requests", { resource_type: "kasbon", resource_id: id, label: "kasbon" });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Kasbon dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <PageSkeleton stats={1} rows={5} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Kasbon Karyawan</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Pencatatan pinjaman karyawan dan pelunasannya.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-kasbon-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Catat Kasbon</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Kasbon Baru</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label>Karyawan</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="kasbon-employee"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                  <SelectContent>
                    {employees.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">Belum ada karyawan</div>}
                    {employees.map((e) => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Jumlah (Rp)</Label><Input data-testid="kasbon-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" /></div>
              <div className="space-y-1.5"><Label>Alasan</Label><Input data-testid="kasbon-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="cth. Pinjaman biaya sekolah" /></div>
              <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={submit} data-testid="kasbon-submit" variant="gold">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 bg-rose-950 text-white relative overflow-hidden max-w-md">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-rose-500/20 blur-3xl" />
        <div className="flex items-center gap-2 relative"><HandCoins className="h-4 w-4 text-rose-200" /><p className="text-xs text-rose-200">Total Kasbon Belum Lunas</p></div>
        <p data-testid="kasbon-outstanding" className="font-heading text-2xl font-extrabold mt-1.5 relative">{formatIDR(data.total_outstanding)}</p>
      </Card>

      <Card className="overflow-hidden">
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Karyawan</TableHead>
                <TableHead>Alasan</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right">Sisa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Belum ada kasbon.</TableCell></TableRow>}
              {data.items.map((k) => (
                <TableRow key={k._id} data-testid={`kasbon-row-${k._id}`}>
                  <TableCell className="font-medium">{k.employee_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{k.reason || "-"}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(k.date)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{formatIDR(k.amount)}</TableCell>
                  <TableCell className={`text-right font-semibold whitespace-nowrap ${k.remaining > 0 ? "text-rose-600" : "text-emerald-600"}`}>{formatIDR(k.remaining)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={k.status === "Lunas"
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"
                      : "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300"}>{k.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {k.status !== "Lunas" && (
                        <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => { setPayFor(k); setPayAmount(""); }} data-testid={`kasbon-pay-${k._id}`}>
                          <Wallet className="h-3.5 w-3.5" /> Bayar
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(k._id)} data-testid={`kasbon-delete-${k._id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden divide-y divide-border">
          {data.items.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">Belum ada kasbon.</p>}
          {data.items.map((k) => (
            <div key={k._id} data-testid={`kasbon-row-${k._id}`} className="p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{k.employee_name}</p>
                  <p className="text-[11px] text-muted-foreground">{formatDate(k.date)}{k.reason ? ` · ${k.reason}` : ""}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${k.status === "Lunas"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"
                  : "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300"}`}>{k.status}</Badge>
              </div>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-muted-foreground">Jumlah: {formatIDR(k.amount)}</span>
                <span className={`font-semibold ${k.remaining > 0 ? "text-rose-600" : "text-emerald-600"}`}>Sisa: {formatIDR(k.remaining)}</span>
              </div>
              <div className="flex justify-end gap-1 pt-0.5">
                {k.status !== "Lunas" && (
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => { setPayFor(k); setPayAmount(""); }} data-testid={`kasbon-pay-${k._id}`}>
                    <Wallet className="h-3 w-3" /> Bayar
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2 text-rose-600 hover:text-rose-700" onClick={() => remove(k._id)} data-testid={`kasbon-delete-${k._id}`}>
                  <Trash2 className="h-3 w-3" /> Hapus
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Bayar / Cicil Kasbon</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">{payFor.employee_name} — sisa <b className="text-foreground">{formatIDR(payFor.remaining)}</b></p>
              <div className="space-y-1.5"><Label>Jumlah Pembayaran (Rp)</Label><Input data-testid="kasbon-pay-amount" type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Batal</Button>
            <Button onClick={pay} data-testid="kasbon-pay-submit" variant="gold">Simpan Pembayaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
